/**
 * What is hidden from CS for the MVP (MVP-SCOPE, 6 Nov 2026, PR-58). The code, its tests and the
 * saved data stay; only the way to *start* these is taken out of what CS can see or reach. A
 * saved collector that already uses one (an Apify collector, a term set) still opens, runs and
 * shows. No flag service and no environment switch: after the MVP, remove an entry here and the
 * feature is back everywhere that reads this list.
 */
export const HIDDEN_FOR_MVP = [
  {
    id: "usage",
    why: "One cost line in the Crawler is enough; the Usage and cost console comes after the MVP.",
  },
  {
    id: "termSets",
    why: "Keywords inline cover the MVP; term set pages and pickers come later.",
  },
  {
    id: "skippedList",
    why: "Ops depth after the MVP; a run's own view still says why a page was dropped.",
  },
  {
    id: "liveCollectors",
    why: "Ops depth after the MVP; Workers and Today stay.",
  },
  {
    id: "journeyView",
    why: "The page journey says it is not available yet; its entry point waits for the first release.",
  },
  {
    id: "searchPresets",
    why: "Keyword news, Find & read, Topic feed and Research database are not the watcher MVP.",
  },
  {
    id: "templateApify",
    why: "Firecrawl covers the JavaScript and no-feed cases.",
  },
  {
    id: "templateExa",
    why: "Untested (no docs or key here); back after the first release.",
  },
  {
    id: "patternTester",
    why: "Nice-to-have; ghost-chip suggestions stay.",
  },
  {
    id: "costWarnings",
    why: "The single cost line in the Crawler is enough.",
  },
  {
    id: "versionsAndDrafts",
    why: "Versions and staged drafts are power features.",
  },
  {
    id: "fallbackPipelines",
    why: "Power feature; the editor shows one pipeline.",
  },
  {
    id: "stepAlternatives",
    why: "Power feature; the editor shows one pipeline.",
  },
  {
    id: "qualityStrictness",
    why: "Required and Warnings with Keep / Hold / Drop are enough.",
  },
  {
    id: "folderBelowOneLevel",
    why: "One level of workspaces for the MVP; deeper folders come with Organise.",
  },
] as const;

export type HiddenFeatureId = (typeof HIDDEN_FOR_MVP)[number]["id"];

/** Whether a feature is hidden for the MVP: the one question every entry point asks. */
export const isHiddenForMvp = (id: HiddenFeatureId): boolean =>
  HIDDEN_FOR_MVP.some((entry) => entry.id === id);

/** The template cards shown (Our crawler and Firecrawl; "Build your own" is its own card). */
export const templateIsHidden = (templateId: string): boolean =>
  (templateId === "apify" && isHiddenForMvp("templateApify")) ||
  (templateId === "exa" && isHiddenForMvp("templateExa"));

/** The only preset a new collector can start from; the search presets are hidden. */
export const presetIsHidden = (presetId: string): boolean =>
  isHiddenForMvp("searchPresets") && presetId !== "watch_sites";
